import React from 'react';
import { Beer, Droplet, MapPin, Gauge, ClipboardList, Info, Scale, History, Home, QrCode } from 'lucide-react';
import { /* KegIcon, */ TapIcon, MaintenanceIcon, TAP_STATUSES, CLEANING_STATUSES, renderStars, calculateAverageRating, getTotalRatings, calculateFullWeight /*, getSelectableLocations, parseLocationValue */ } from './kegops-utils';
import { normalizeString } from './utils';

export const KegLandingView = ({
  keg,
  account,
  selectedBrewery,
  settings,
  onTapStatusChange,
  onCleaningStatusChange,
  onLocationChange,
  onMarkEmpty,
  onRateBeer,
  onCleanAndSanitize,
  onFillKeg,
  onEditBeer,
  onViewHistory,
  onOpenQRModal,
  onAddLocation,
  onEditTapAssignment,
  setCurrentView
}) => {
  if (!keg) return null;

  const tapNumber = keg.tapNumber;
  const isTapNumeric = tapNumber !== undefined && tapNumber !== null && /^\d+$/.test(String(tapNumber).trim());

  const getDaysOnTapLabel = (keg) => {
    const started = keg.current_beer_started_at ? new Date(keg.current_beer_started_at) : null;
    if (!started) return null;
    const now = new Date();
    const diffMs = now - started;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours >= 1) return `${hours}h`;
    const minutes = Math.floor(diffMs / (1000 * 60));
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white h-24">
          <div className="flex items-center justify-between h-full px-4">
            <div className="flex items-center gap-2 h-full">
              {selectedBrewery?.logo_url ? (
                <img src={selectedBrewery.logo_url} alt={selectedBrewery.name} className="h-20 w-20 object-contain rounded-full" />
              ) : (
                <img src="/kegops-logo.png" alt="KegOps" className="h-full object-contain" />
              )}
              <div className="flex flex-col justify-center h-full">
                <h1 className="text-3xl font-bold">Keg Details</h1>
                <h2 className="text-lg">Keg #{keg.kegNumber} — "{keg.name}"</h2>
                <p className="text-amber-100 text-sm">{keg.owner}</p>
              </div>
            </div>
            <button
              onClick={() => setCurrentView('dashboard')}
              className="flex items-center gap-2 bg-amber-800 hover:bg-amber-900 px-4 py-2 rounded transition"
            >
              <Home className="w-4 h-4" />
              Dashboard
            </button>
          </div>
        </div>

        {/* Status Summary */}
        <div className="bg-amber-50 dark:bg-amber-900 dark:bg-opacity-20 p-4 border-b dark:border-gray-700">
          <h3 className="font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Gauge className="w-5 h-5" />
            STATUS SUMMARY
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-gray-900 dark:text-gray-100">
              <span className="font-medium">Tap Status:</span> 
              <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">{keg.tapStatus}</span>
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              <span className="font-medium">Cleaning:</span> 
              <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">{keg.cleaningStatus}</span>
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              <span className="font-medium">Batch Count:</span> {keg.batchCount}
            </div>
          </div>
        </div>

        {/* Current Beer */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Beer className="w-5 h-5" />
            CURRENT BEER
          </h3>
          {keg.isEmpty ? (
            <div className="text-center py-8">
              <p className="text-xl font-semibold text-gray-500 mb-4">Keg is Empty</p>
              {(keg.cleaningStatus === "Sanitized" || keg.cleaningStatus === "Purged") ? (
                <button
                  onClick={onFillKeg}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-medium flex items-center justify-center gap-2 mx-auto"
                >
                  <Beer className="w-5 h-5" />
                  Fill Me Up
                </button>
              ) : (
                <button
                  onClick={onCleanAndSanitize}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium flex items-center justify-center gap-2 mx-auto"
                >
                  <Droplet className="w-5 h-5" />
                  Clean and Sanitize Me
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {keg.currentBeer.imageUrl && (
                <div className="mb-3 flex justify-center">
                  <img 
                    src={keg.currentBeer.imageUrl} 
                    alt={keg.currentBeer.name}
                    className="max-w-full max-h-64 w-auto h-auto object-contain rounded-lg"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">Name:</span>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{keg.currentBeer.name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">Style:</span>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{keg.currentBeer.style}</p>
                </div>
              </div>
              {keg.currentBeer.description && (
                <div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">Description:</span>
                  <p className="text-sm mt-1 text-gray-900 dark:text-gray-100">{keg.currentBeer.description}</p>
                </div>
              )}

              {/* Days on Tap */}
              {keg.current_beer_started_at && (
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">On tap:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{getDaysOnTapLabel(keg)}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-gray-700 dark:text-gray-300">ABV:</span>
                  <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">{keg.currentBeer.abv}%</span>
                </div>
                {keg.currentBeer.ibu && (
                  <div>
                    <span className="text-gray-700 dark:text-gray-300">IBU:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">{keg.currentBeer.ibu}</span>
                  </div>
                )}
                {keg.currentBeer.og && (
                  <div>
                    <span className="text-gray-700 dark:text-gray-300">OG:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">{keg.currentBeer.og}</span>
                  </div>
                )}
                {keg.currentBeer.fg && (
                  <div>
                    <span className="text-gray-700 dark:text-gray-300">FG:</span>
                    <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">{keg.currentBeer.fg}</span>
                  </div>
                )}
              </div>
              <button 
                onClick={onMarkEmpty}
                className="w-full mt-3 bg-red-600 text-white py-2 rounded hover:bg-red-700 transition font-medium flex items-center justify-center gap-2"
              >
                <span>Keg is Kicked</span>
                <Beer className="w-5 h-5 rotate-180" />
              </button>
              <div className="flex gap-2">
                <button 
                  onClick={onRateBeer}
                  className="flex-1 mt-2 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition font-medium flex items-center justify-center gap-2"
                >
                  <span className="w-5 h-5">⭐</span>
                  Rate This Beer
                </button>
                <button
                  onClick={onEditBeer}
                  className="flex-1 mt-2 bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition font-medium flex items-center justify-center gap-2"
                >
                  Edit Beer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tap Status + Location */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <div className="grid grid-cols-2 gap-4">
            {/* Left: Tap Status */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <TapIcon className="w-5 h-5" />
                TAP STATUS
              </h3>
              <div className="space-y-2">
                {TAP_STATUSES.map(status => (
                  <label key={status.value} className="flex items-center gap-2 cursor-pointer text-gray-900 dark:text-gray-100">
                    <input
                      type="radio"
                      name="tapStatus"
                      checked={normalizeString(keg.tapStatus) === normalizeString(status.value)}
                      onChange={() => onTapStatusChange(status.value)}
                      className="w-4 h-4"
                      disabled={keg.isEmpty && status.value !== "Empty"}
                    />
                    <span className={keg.isEmpty && status.value !== "Empty" ? 'text-gray-400 dark:text-gray-600' : ''}>
                      {status.value}
                    </span>
                    {status.days && <span className="text-sm text-gray-600 dark:text-gray-400">(+days)</span>}
                  </label>
                ))}
              </div>
            </div>

            {/* Right: Location (centered) */}
            <div className="flex flex-col items-center h-full">
              <h3 className="font-semibold mb-3 flex items-center justify-center gap-2 text-gray-900 dark:text-gray-100">
                <MapPin className="w-5 h-5" />
                LOCATION
              </h3>
              {normalizeString(keg.tapStatus) === 'on tap' ? (
                <>
                  <select
                    value={`${keg.location || ''}|${keg.sublocation || ''}`}
                    onChange={(e) => {
                      const [loc, sub] = e.target.value.split('|');
                      onLocationChange(keg, loc, sub);
                    }}
                    className="w-full border rounded px-3 py-2 text-sm text-center focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="|">Select location</option>
                    {settings.locations?.map(loc => {
                      if (loc.sublocations && loc.sublocations.length > 0) {
                        return loc.sublocations.map(sub => (
                          <option key={`${loc.id}-${sub.id}`} value={`${loc.name}|${sub.name}`}>
                            {loc.name} → {sub.name}
                          </option>
                        ));
                      } else {
                        return (
                          <option key={loc.id} value={`${loc.name}|`}>
                            {loc.name}
                          </option>
                        );
                      }
                    })}
                  </select>
                  <div className="flex-1 flex items-center justify-center mt-2 w-full">
                    <button 
                      onClick={onEditTapAssignment}
                      className="text-center hover:bg-amber-50 dark:hover:bg-gray-700 rounded p-2 transition cursor-pointer"
                    >
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-semibold mb-1">{isTapNumeric ? 'Tap Number' : 'Tap'}</div>
                      <div className="text-4xl font-extrabold text-amber-600 hover:text-amber-700 dark:hover:text-amber-300">{tapNumber}</div>
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-center text-sm text-gray-400 dark:text-gray-600 w-full">—</div>
              )}
            </div>
          </div>
        </div>

        {/* Cleaning Status */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Droplet className="w-5 h-5" />
            CLEANING STATUS
          </h3>
          <div className="flex flex-wrap gap-2">
            {CLEANING_STATUSES.map(status => (
              <button
                key={status}
                onClick={() => onCleaningStatusChange(status)}
                className={`px-3 py-1 rounded transition ${
                  keg.cleaningStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Keg Details */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Info className="w-5 h-5" />
            KEG DETAILS
          </h3>
          <div className="space-y-1 text-sm">
            <div className="text-gray-900 dark:text-gray-100"><span className="text-gray-700 dark:text-gray-300">Type:</span> <span className="ml-2">{keg.kegDetails.type}</span></div>
            <div className="text-gray-900 dark:text-gray-100"><span className="text-gray-700 dark:text-gray-300">Volume:</span> <span className="ml-2">{keg.kegDetails.volume} {settings.volumeUnit}</span></div>
            <div className="text-gray-900 dark:text-gray-100"><span className="text-gray-700 dark:text-gray-300">Features:</span> <span className="ml-2">{keg.kegDetails.features.join(", ") || "None"}</span></div>
          </div>
        </div>

        {/* Weights */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Scale className="w-5 h-5" />
            WEIGHTS
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-gray-900 dark:text-gray-100">
              <span className="text-gray-700 dark:text-gray-300">Tare Weight:</span>
              <p className="font-medium">
                {keg.weights.tare ? `${keg.weights.tare} ${settings.weightUnit}` : 'Not set'}
              </p>
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              <span className="text-gray-700 dark:text-gray-300">Full Weight:</span>
              <p className="font-medium">
                {keg.weights.tare ? 
                  `${calculateFullWeight(keg.weights.tare, keg.kegDetails.volume, settings)} ${settings.weightUnit} (calculated)` : 
                  'N/A'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Maintenance */}
        {keg.maintenanceRecords && keg.maintenanceRecords.length > 0 && (
          <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <MaintenanceIcon className="w-5 h-5" />
              MAINTENANCE
            </h3>
            <div className="space-y-1 text-sm">
              <p className="text-gray-700 dark:text-gray-300">Last serviced: {keg.maintenanceRecords[keg.maintenanceRecords.length - 1].date}</p>
              <p className="text-gray-700 dark:text-gray-300">
                Items: {keg.maintenanceRecords[keg.maintenanceRecords.length - 1].items.join(", ")}
              </p>
            </div>
          </div>
        )}

        {/* History */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <History className="w-5 h-5" />
            HISTORY (last 5 locations)
          </h3>
          <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
            {keg.locationHistory.map((hist, idx) => (
              <li key={idx}>• {hist.location} → {hist.date}</li>
            ))}
          </ul>
        </div>

        {/* Beer History */}
        <div className="p-4 border-b dark:border-gray-700 dark:text-gray-100">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <ClipboardList className="w-5 h-5" />
            BEER HISTORY (never resets)
          </h3>
          {keg.beerHistory.length > 0 ? (
            <ul className="space-y-2">
              {keg.beerHistory.map((beer, idx) => (
                <li key={idx}>
                  <button 
                    onClick={() => onViewHistory(beer)}
                    className="flex items-center gap-2 hover:text-amber-600 transition text-left w-full text-gray-700 dark:text-gray-300"
                  >
                    <span>• {beer.name} —</span>
                    {renderStars(calculateAverageRating(beer.sessions))}
                    <span className="text-xs text-gray-600 dark:text-gray-400 ml-1">
                      ({getTotalRatings(beer.sessions)} rating{getTotalRatings(beer.sessions) !== 1 ? 's' : ''})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">No beer history yet</p>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="p-4 flex gap-2">
          <button 
            onClick={() => setCurrentView('dashboard')}
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" />
            Dashboard
          </button>
          <button 
            onClick={() => onOpenQRModal(keg)}
            className="flex-1 bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition flex items-center justify-center gap-2"
          >
            <QrCode className="w-4 h-4" />
            QR Code
          </button>
        </div>
      </div>
    </div>
  );
};